import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { Button } from '@shared/components/ui/button';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useTranslation } from 'react-i18next';
import { ReturnedModel } from '../../models/Returned';
import { Link } from 'react-router-dom';
import {
  getReturnedStatusName,
  getReturnedStatusBadgeClass,
  getReturnedActionButtonText,
  shouldShowReturnedActionButton,
  getReturnedActionType,
} from '../../config';

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<ReturnedModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
  onViewClick?: (item: ReturnedModel) => void;
  onLocateClick?: (item: ReturnedModel) => void;
  onReceiveClick?: (item: ReturnedModel) => void;
}

// 🎯 Componente de card individual para Returned - SRP
export const Card = ({ item: returned, className = '', showActions = true }: CardProps) => {
  const { t } = useTranslation('returnedModule');

  const { canAccess } = usePermissions();

  // 🎯 Verificar permisos de devoluciones (solo tenant y tenant_3pl)
  const canReceive = canAccess(['tenant', 'tenant_3pl'], ['returns.receive']);
  const canLocate = canAccess(['tenant', 'tenant_3pl'], ['returns.locate']);
  const canView = canAccess(['tenant', 'tenant_3pl'], ['returns.view']);

  // 🎯 Función para renderizar las acciones usando la configuración
  const renderActions = () => {
    const actionType = getReturnedActionType(returned.status);

    if (!shouldShowReturnedActionButton(returned.status)) {
      return null;
    }

    if (actionType === 'view') {
      // Ver requiere returns.view
      if (!canView) return null;
      return (
        <Link to={`/returned/${returned.id}/receive`} className="w-full" data-testid="view-returned-button">
          <Button type="button" variant="outline" size="sm" className="w-full">
            {getReturnedActionButtonText(returned.status, t)}
          </Button>
        </Link>
      );
    } else if (actionType === 'locate') {
      // Ubicar requiere returns.locate
      if (!canLocate) return null;
      return (
        <Link to={`/returned/${returned.id}/receive`} className="w-full" data-testid="locate-returned-button">
          <Button type="button" variant="outline" size="sm" className="w-full">
            {getReturnedActionButtonText(returned.status, t)}
          </Button>
        </Link>
      );
    } else if (actionType === 'receive') {
      // Recepcionar requiere returns.receive
      if (!canReceive) return null;
      return (
        <Link to={`/returned/${returned.id}/receive`} className="w-full" data-testid="receive-returned-button">
          <Button type="button" variant="outline" size="sm" className="w-full">
            {getReturnedActionButtonText(returned.status, t)}
          </Button>
        </Link>
      );
    }

    return null;
  };

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      <div className="space-y-3">
        {/* Código de entrada */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-title">{returned.code}</span>
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getReturnedStatusBadgeClass(returned.status)}`}
          >
            {getReturnedStatusName(returned.status, t)}
          </span>
        </div>

        {/* Fecha */}
        <div className="text-sm text-subtext">
          {new Date(returned.created_at).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>

        {/* Pedidos */}
        <div className="text-sm text-subtext">
          {t('table.orders')}: {returned.orders_count}
        </div>

        {/* Acciones */}
        {showActions && <div className="flex justify-end pt-2">{renderActions()}</div>}
      </div>
    </div>
  );
};
